import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";
import { LETTERS } from "@/data/letters";

const fmt = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function Letters() {
  const [lead, ...rest] = LETTERS;

  return (
    <main className="pt-20" data-testid="letters-page">
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(169,129,47,0.13), transparent 62%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="eyebrow mb-6">Kintsugi Letters</p>
            <h1
              className="font-display text-ink font-medium leading-[1.06] max-w-3xl"
              style={{ fontSize: "clamp(2.4rem, 5.2vw, 4.2rem)" }}
              data-testid="letters-heading"
            >
              Short reflections, written slowly
            </h1>
            <p className="mt-8 text-inkdim leading-relaxed max-w-2xl text-[15px] md:text-base">
              Notes on patterns, protection and the analog life — the things worth
              saying at length, published when they're finished rather than on a schedule.
            </p>
            <GoldSeam variant="horizontal" className="mt-10 w-56 h-10 opacity-70" delay={0.2} />
          </Reveal>
        </div>
      </section>

      <section className="pb-8" data-testid="letters-lead-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Link
              to={`/letters/${lead.slug}`}
              data-testid={`letter-card-${lead.slug}`}
              className="group grid lg:grid-cols-2 gap-10 lg:gap-16 items-center border border-[rgba(169,129,47,0.26)] hover:border-gold transition-colors duration-500 p-4 md:p-6"
            >
              <div className="overflow-hidden">
                <img
                  src={lead.image}
                  alt={lead.title}
                  className="w-full h-[280px] md:h-[400px] object-cover group-hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                />
              </div>
              <div className="md:pr-8">
                <p className="text-[10px] uppercase tracking-[0.24em] text-inkdim mb-5">
                  Latest · {fmt(lead.date)} · {lead.read}
                </p>
                <h2 className="font-display text-ink font-medium leading-[1.12] text-3xl md:text-4xl group-hover:text-gold transition-colors duration-500">
                  {lead.title}
                </h2>
                <p className="mt-5 text-inkdim leading-relaxed text-[15px]">{lead.dek}</p>
                <span className="inline-flex items-center gap-2 mt-8 text-[10px] uppercase tracking-[0.24em] text-gold group-hover:gap-3 transition-all duration-500">
                  Read the letter <ArrowRight size={12} strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="letters-grid-section">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {rest.map((l, i) => (
            <Reveal key={l.slug} delay={i * 0.07}>
              <Link
                to={`/letters/${l.slug}`}
                data-testid={`letter-card-${l.slug}`}
                className="group flex flex-col h-full border border-[rgba(169,129,47,0.26)] bg-paper hover:border-gold transition-colors duration-500"
              >
                <div className="overflow-hidden">
                  <img
                    src={l.image}
                    alt={l.title}
                    loading="lazy"
                    className="w-full h-44 object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out"
                  />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-inkdim mb-4">
                    {fmt(l.date)} · {l.read}
                  </p>
                  <h3 className="font-display text-2xl text-ink leading-snug group-hover:text-gold transition-colors duration-500">
                    {l.title}
                  </h3>
                  <p className="mt-3 text-inkdim text-sm leading-relaxed flex-1">{l.dek}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-[10px] uppercase tracking-[0.24em] text-gold group-hover:gap-3 transition-all duration-500">
                    Read <ArrowRight size={12} strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
